import { useEffect, useState, useCallback, useRef } from "react";
import {
  Activity, AlertTriangle, Cpu, HardDrive, MemoryStick, RefreshCw,
  Trash2, Pause, Clock, Server, Zap, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { api, apiErrorMessage } from "../lib/api";

// Poll cadence. Low-noise: 5s for metrics/inflight, 15s for history, on demand
// for stuck-jobs. Tabs out of view pause so we don't hammer the backend.
const POLL_METRICS_MS = 5000;
const POLL_INFLIGHT_MS = 5000;
const POLL_HISTORY_MS = 15000;

function Bar({ percent, danger = 70, warn = 50 }) {
  const safe = Math.max(0, Math.min(100, percent || 0));
  const tone =
    safe >= danger ? "bg-red-500"
    : safe >= warn ? "bg-amber-400"
    : "bg-emerald-500";
  return (
    <div className="h-1.5 w-full rounded bg-zinc-800 overflow-hidden">
      <div className={`h-full ${tone} transition-all duration-500`}
           style={{ width: `${safe}%` }} />
    </div>
  );
}

function Stat({ label, value, sub, right }) {
  return (
    <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        {right}
      </div>
      <div className="text-xl font-mono mt-1 text-zinc-100">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Métricas ─────────────────────────────────────────────────────────────
function MetricsCard({ onRestart }) {
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const [restarting, setRestarting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/metrics");
      setM(data); setErr("");
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_METRICS_MS);
    return () => clearInterval(id);
  }, [load]);

  const restart = async () => {
    if (!window.confirm("Esto reinicia el backend. Las peticiones en vuelo se cortarán. ¿Continuar?")) return;
    setRestarting(true);
    try {
      await api.post("/admin/restart-backend");
      toast.success("Reinicio programado — el backend volverá en 2-5 s");
      onRestart?.();
      // Backend will be unreachable for a few seconds; re-poll.
      setTimeout(load, 4000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setRestarting(false); }
  };

  if (err) return (
    <div className="rounded border border-red-900/50 bg-red-950/30 p-3 text-xs text-red-300" data-testid="admin-metrics-error">
      {err}
    </div>
  );
  if (!m) return <div className="text-xs text-zinc-500">Cargando métricas…</div>;

  const hmem = m.host.memory;
  const pmem = m.process.memory;
  const pod = m.pod;  // cgroup-scoped memory for THIS pod, may be null
  return (
    <div data-testid="admin-metrics" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-zinc-100 inline-flex items-center gap-2">
          <Activity className="h-4 w-4 text-emerald-400" /> Recursos
        </h2>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost"
                  data-testid="admin-metrics-refresh"
                  onClick={load}>
            <RefreshCw className="h-3 w-3 mr-1.5" /> Actualizar
          </Button>
          <Button size="sm"
                  data-testid="admin-metrics-restart"
                  onClick={restart} disabled={restarting}
                  className="bg-red-600 hover:bg-red-500 text-white">
            <Server className="h-3 w-3 mr-1.5" />
            {restarting ? "Reiniciando…" : "Reiniciar backend"}
          </Button>
        </div>
      </div>

      {/*
        Honest-metrics principle: show what belongs to THIS pod first
        (cgroup-scoped RAM + backend process), then the shared host
        values with a clear caveat. Prevents the "why did RAM jump to
        62% while in-flight is 0?" confusion caused by a neighbor build
        running on the same Kubernetes node.
      */}
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1.5">
          <Zap className="h-3 w-3" /> KRAI CONNECT (tu pod)
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {pod ? (
            <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
                <MemoryStick className="h-3 w-3" /> RAM del pod
              </div>
              <div className="text-xl font-mono mt-1">{pod.percent.toFixed(1)}%</div>
              <Bar percent={pod.percent} />
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                {pod.used.pretty} / {pod.limit.pretty} · cgroup
              </div>
            </div>
          ) : (
            <div className="rounded border border-zinc-800 bg-zinc-950 p-3 text-[11px] text-zinc-500">
              Sin cgroup límite · entorno dev
            </div>
          )}
          <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
              <Cpu className="h-3 w-3" /> Proceso backend
            </div>
            <div className="text-xl font-mono mt-1">{pmem.rss.pretty}</div>
            <div className="text-[10px] text-zinc-500 mt-1 font-mono">
              RSS · PID {m.process.pid} · {m.process.threads} threads
            </div>
          </div>
          <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" /> Uptime
            </div>
            <div className="text-xl font-mono mt-1">
              {Math.floor(m.process.uptime_seconds / 60)}m {m.process.uptime_seconds % 60}s
            </div>
            <div className="text-[10px] text-zinc-500 mt-1 font-mono">
              CPU proceso {m.process.cpu_percent.toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
            <Server className="h-3 w-3" /> Nodo Kubernetes {m.host.shared && "(compartido)"}
          </div>
          {m.host.shared && (
            <div className="text-[10px] text-amber-400/80 italic">
              Estas cifras suben con builds/deploys vecinos — no reflejan tu app.
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
            <div className="flex items-baseline justify-between gap-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
                <Cpu className="h-3 w-3" /> CPU host
              </div>
              <span className="text-[10px] font-mono text-zinc-500">{m.host.cpu_count}×</span>
            </div>
            <div className="text-xl font-mono mt-1">{m.host.cpu_percent.toFixed(1)}%</div>
            <Bar percent={m.host.cpu_percent} />
            <div className="text-[10px] text-zinc-500 mt-1 font-mono">
              load {m.host.load_avg["1m"].toFixed(2)} · {m.host.load_avg["5m"].toFixed(2)} · {m.host.load_avg["15m"].toFixed(2)}
            </div>
          </div>
          <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
              <MemoryStick className="h-3 w-3" /> RAM host
            </div>
            <div className="text-xl font-mono mt-1">{hmem.percent.toFixed(1)}%</div>
            <Bar percent={hmem.percent} />
            <div className="text-[10px] text-zinc-500 mt-1 font-mono">
              {hmem.used.pretty} / {hmem.total.pretty}
            </div>
          </div>
          {m.host.disk && (
            <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
                <HardDrive className="h-3 w-3" /> Disco host
              </div>
              <div className="text-xl font-mono mt-1">{m.host.disk.percent.toFixed(1)}%</div>
              <Bar percent={m.host.disk.percent} />
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                {m.host.disk.used.pretty} / {m.host.disk.total.pretty}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="text-[11px] text-zinc-500 font-mono">
        telemetry · in-flight: {m.telemetry.inflight_count} · singleflight activo: {m.telemetry.singleflight_active} · GPU: no aplica
      </div>
    </div>
  );
}

// ─── In-flight ────────────────────────────────────────────────────────────
function InflightCard() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [cancelling, setCancelling] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/inflight");
      setRows(data.items || []);
      setErr("");
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INFLIGHT_MS);
    return () => clearInterval(id);
  }, [load]);

  const cancel = async (id) => {
    setCancelling((c) => ({ ...c, [id]: true }));
    try {
      await api.post(`/admin/inflight/${id}/cancel`);
      toast.success("Cancelación solicitada. Se aplicará en el próximo checkpoint del adaptador.");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setCancelling((c) => ({ ...c, [id]: false })); }
  };

  return (
    <div data-testid="admin-inflight" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-zinc-100 inline-flex items-center gap-2">
          <Clock className="h-4 w-4 text-sky-400" /> Peticiones en curso
          <span className="text-xs text-zinc-500 font-mono">({rows.length})</span>
        </h2>
        <Button size="sm" variant="ghost" onClick={load}>
          <RefreshCw className="h-3 w-3 mr-1.5" /> Actualizar
        </Button>
      </div>
      {err && <div className="text-xs text-red-400">{err}</div>}
      {rows.length === 0 ? (
        <div className="rounded border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-500 text-center">
          Ninguna petición en vuelo ahora mismo.
        </div>
      ) : (
        <div className="rounded border border-zinc-800 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">App</th>
                <th className="text-left px-3 py-2">Task</th>
                <th className="text-left px-3 py-2">Ruta</th>
                <th className="text-right px-3 py-2">Elapsed</th>
                <th className="text-right px-3 py-2 w-24">Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-zinc-800">
                  <td className="px-3 py-2 font-mono">{r.app_id}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex rounded border border-emerald-500/25 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-mono text-emerald-300">
                      {r.task || "chat"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-400">{r.path}</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-300">
                    {r.elapsed_ms} ms
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => cancel(r.id)}
                      disabled={r.cancel_requested || cancelling[r.id]}
                      data-testid={`admin-inflight-cancel-${r.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Pause className="h-3 w-3" />
                      {r.cancel_requested ? "Pedido" : "Cancelar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Historial ────────────────────────────────────────────────────────────
function HistoryCard() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [appId, setAppId] = useState("");
  const [limit, setLimit] = useState(50);
  const [err, setErr] = useState("");
  // Each row's expand state is user-controlled but we PRE-SEED error
  // rows to open so the operator sees failures without clicking —
  // common ask: "I came here because something failed, show it".
  const [expanded, setExpanded] = useState({});
  const seededRef = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (status) q.set("status", status);
      if (appId) q.set("app_id", appId);
      q.set("limit", limit);
      const { data } = await api.get(`/admin/history?${q.toString()}`);
      const items = data.items || [];
      setRows(items);
      setErr("");
      // Auto-expand any NEW error row we haven't seen yet in this
      // session. We dedupe via seededRef so the user can collapse a
      // row manually and it stays collapsed on the next poll.
      setExpanded((prev) => {
        const next = { ...prev };
        for (const it of items) {
          if (it.status === "error" && it.error && !seededRef.current.has(it.id)) {
            seededRef.current.add(it.id);
            if (next[it.id] === undefined) next[it.id] = true;
          }
        }
        return next;
      });
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, [status, appId, limit]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_HISTORY_MS);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div data-testid="admin-history" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-zinc-100">Historial reciente</h2>
        <div className="flex items-center gap-2">
          <select
            data-testid="admin-history-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200"
          >
            <option value="">Todos</option>
            <option value="ok">Solo OK</option>
            <option value="error">Solo Error</option>
          </select>
          <input
            data-testid="admin-history-appid"
            placeholder="app_id…"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200 w-32 font-mono"
          />
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
          <Button size="sm" variant="ghost" onClick={load}>
            <RefreshCw className="h-3 w-3 mr-1.5" /> Actualizar
          </Button>
        </div>
      </div>
      {err && <div className="text-xs text-red-400">{err}</div>}
      {rows.length === 0 ? (
        <div className="rounded border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-500 text-center">
          Sin actividad registrada todavía.
        </div>
      ) : (
        <div className="rounded border border-zinc-800 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 w-6"></th>
                <th className="text-left px-3 py-2">Hora</th>
                <th className="text-left px-3 py-2">App</th>
                <th className="text-left px-3 py-2">Task</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-right px-3 py-2">HTTP</th>
                <th className="text-right px-3 py-2">Latencia</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = expanded[r.id];
                return (
                  <>
                    <tr key={r.id}
                        className={`border-t border-zinc-800 ${r.status === "error" ? "bg-red-950/10" : ""} ${r.error ? "cursor-pointer" : ""}`}
                        onClick={() => r.error && setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}>
                      <td className="px-3 py-2 text-zinc-500">
                        {r.error ? (open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />) : null}
                      </td>
                      <td className="px-3 py-2 font-mono text-zinc-400">
                        {new Date(r.finished_at).toLocaleTimeString()}
                      </td>
                      <td className="px-3 py-2 font-mono">{r.app_id}</td>
                      <td className="px-3 py-2">{r.task || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-mono border ${r.status === "ok" ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.http_status}</td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-400">{r.latency_ms} ms</td>
                    </tr>
                    {open && r.error && (
                      <tr className="bg-zinc-950">
                        <td></td>
                        <td colSpan={6} className="px-3 py-2 text-[11px] font-mono text-red-300 whitespace-pre-wrap break-all">
                          {r.error}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Latency stats ────────────────────────────────────────────────────────
// Compact P50/P95/P99 + error-rate table grouped by task. Computed
// server-side (`/admin/history/stats`) over the last N requests so this
// card stays O(1) on the frontend regardless of traffic volume.
function LatencyCard() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/history/stats?window=200");
      setStats(data); setErr("");
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const tone = (ms) =>
    ms >= 10000 ? "text-red-400"
    : ms >= 3000 ? "text-amber-300"
    : "text-emerald-300";

  return (
    <div data-testid="admin-latency" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-zinc-100 inline-flex items-center gap-2">
          <Activity className="h-4 w-4 text-indigo-400" /> Latencias
          <span className="text-xs text-zinc-500 font-mono">
            (últimas {stats?.total ?? 0} peticiones)
          </span>
        </h2>
        <Button size="sm" variant="ghost" onClick={load}>
          <RefreshCw className="h-3 w-3 mr-1.5" /> Actualizar
        </Button>
      </div>
      {err && <div className="text-xs text-red-400">{err}</div>}
      {!stats || stats.by_task.length === 0 ? (
        <div className="rounded border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-500 text-center">
          Sin datos todavía — las estadísticas aparecen cuando el gateway recibe peticiones.
        </div>
      ) : (
        <div className="rounded border border-zinc-800 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Task</th>
                <th className="text-right px-3 py-2">N</th>
                <th className="text-right px-3 py-2">P50</th>
                <th className="text-right px-3 py-2">P95</th>
                <th className="text-right px-3 py-2">P99</th>
                <th className="text-right px-3 py-2">Max</th>
                <th className="text-right px-3 py-2">Errores</th>
              </tr>
            </thead>
            <tbody>
              {stats.by_task.map((t) => (
                <tr key={t.task} className="border-t border-zinc-800">
                  <td className="px-3 py-2">
                    <span className="inline-flex rounded border border-indigo-500/25 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-mono text-indigo-300">
                      {t.task}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-300">{t.count}</td>
                  <td className={`px-3 py-2 text-right font-mono ${tone(t.p50_ms)}`}>{t.p50_ms} ms</td>
                  <td className={`px-3 py-2 text-right font-mono ${tone(t.p95_ms)}`}>{t.p95_ms} ms</td>
                  <td className={`px-3 py-2 text-right font-mono ${tone(t.p99_ms)}`}>{t.p99_ms} ms</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-500">{t.max_ms} ms</td>
                  <td className="px-3 py-2 text-right font-mono">
                    <span className={t.error_rate > 10 ? "text-red-400" : t.error_rate > 0 ? "text-amber-300" : "text-emerald-300"}>
                      {t.errors} ({t.error_rate}%)
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Stuck jobs ───────────────────────────────────────────────────────────
function StuckJobsCard() {
  const [threshold, setThreshold] = useState(10);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [purging, setPurging] = useState(false);

  const scan = async () => {
    try {
      const { data } = await api.get(`/admin/stuck-jobs?older_than_minutes=${threshold}`);
      setRows(data.items || []);
      setLoaded(true); setErr("");
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  const purge = async () => {
    if (!window.confirm(`Eliminar ${rows.length} job(s) atascado(s) más antiguos de ${threshold} min?`)) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/stuck-jobs/purge", { older_than_minutes: threshold });
      toast.success(`Eliminados: ${data.total}`);
      scan();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setPurging(false); }
  };

  return (
    <div data-testid="admin-stuck-jobs" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-zinc-100 inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-400" /> Jobs atascados en MongoDB
        </h2>
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-400">Más antiguos de</label>
          <input
            type="number"
            min={1}
            max={1440}
            value={threshold}
            onChange={(e) => setThreshold(parseInt(e.target.value, 10) || 10)}
            className="w-20 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs font-mono"
            data-testid="admin-stuck-threshold"
          />
          <span className="text-xs text-zinc-500">min</span>
          <Button size="sm" variant="ghost" onClick={scan} data-testid="admin-stuck-scan">
            <RefreshCw className="h-3 w-3 mr-1.5" /> Escanear
          </Button>
          {rows.length > 0 && (
            <Button size="sm"
                    data-testid="admin-stuck-purge"
                    onClick={purge} disabled={purging}
                    className="bg-red-600 hover:bg-red-500 text-white">
              <Trash2 className="h-3 w-3 mr-1.5" />
              {purging ? "Eliminando…" : `Eliminar (${rows.length})`}
            </Button>
          )}
        </div>
      </div>
      {err && <div className="text-xs text-red-400">{err}</div>}
      {!loaded ? (
        <div className="rounded border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-500 text-center">
          Pulsa <b>Escanear</b> para buscar jobs atascados.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded border border-emerald-500/20 bg-emerald-500/5 p-4 text-xs text-emerald-300 text-center">
          No hay jobs atascados. Todo limpio.
        </div>
      ) : (
        <div className="rounded border border-zinc-800 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Colección</th>
                <th className="text-left px-3 py-2">ID</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-right px-3 py-2">Antigüedad</th>
                <th className="text-left px-3 py-2">Nota</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-zinc-800">
                  <td className="px-3 py-2 font-mono text-zinc-400">{r.store}</td>
                  <td className="px-3 py-2 font-mono">{r.id}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-mono text-amber-300">
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-300">
                    {Math.floor(r.age_seconds / 60)}m
                  </td>
                  <td className="px-3 py-2 text-zinc-500 truncate max-w-xs">{r.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────
export default function AdminPage() {
  return (
    <div data-testid="admin-page" className="space-y-8 pb-10">
      <PageHeader
        eyebrow="Operación"
        title="Administración"
        description="Observa y depura el backend en vivo: recursos, peticiones en curso, historial y jobs huérfanos en MongoDB."
      />

      <section className="space-y-4">
        <MetricsCard />
      </section>

      <section className="space-y-4">
        <InflightCard />
      </section>

      <section className="space-y-4">
        <HistoryCard />
      </section>

      <section className="space-y-4">
        <LatencyCard />
      </section>

      <section className="space-y-4">
        <StuckJobsCard />
      </section>
    </div>
  );
}
